import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SatelliteAlert } from '@greeniq/shared';
import { acknowledgeAlert, getAlerts } from '../api/satellite.js';
import { INDEX_META } from '../lib/satelliteColors.js';
import { DataCard } from './ui/data-card';
import { Badge } from './ui/badge';
import { AlertRow } from './AlertRow';

interface Props {
  courseSlug: string;
}

export function AlertsInbox({ courseSlug }: Props) {
  const [alerts, setAlerts] = useState<SatelliteAlert[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    try {
      const resp = await getAlerts(courseSlug, 'unacknowledged', 10);
      setAlerts(resp.alerts);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [courseSlug]);

  async function onAck(alertId: number) {
    setAcking(alertId);
    try {
      await acknowledgeAlert(courseSlug, alertId);
      setAlerts((prev) => (prev ?? []).filter((a) => a.id !== alertId));
    } finally {
      setAcking(null);
    }
  }

  if (loading) return null;
  if (!alerts || alerts.length === 0) return null;

  return (
    <DataCard
      className="mt-6"
      title="Satellite alerts"
      actions={<Badge variant="destructive">{alerts.length} unacknowledged</Badge>}
    >
      <ul>
        {alerts.map((a) => {
          const meta = INDEX_META[a.indexName];
          return (
            <AlertRow
              key={a.id}
              badge={meta.label}
              badgeStyle={{
                backgroundColor: meta.color,
                color: '#ffffff',
                borderColor: 'transparent',
              }}
              title={describeAlert(a)}
              meta={
                <>
                  Zone #{a.zoneId} · observed {a.observationDate} ·{' '}
                  <Link
                    to={`/app/${courseSlug}/zones/${a.zoneId}`}
                    className="text-greeniq-400 hover:text-greeniq-300"
                  >
                    open zone →
                  </Link>
                </>
              }
              onAcknowledge={() => onAck(a.id)}
              acknowledging={acking === a.id}
            />
          );
        })}
      </ul>
    </DataCard>
  );
}

function describeAlert(a: SatelliteAlert): string {
  if (a.alertType === 'ndre_wow_drop') {
    return `${INDEX_META[a.indexName].label} dropped ${Math.abs(Number(a.deltaPct)).toFixed(1)}% week-over-week`;
  }
  return `${a.alertType} (${INDEX_META[a.indexName].label}, ${Number(a.deltaPct).toFixed(1)}%)`;
}
